import { z } from 'zod';
import { emailSchema, nameSchema } from '../../validation';

/**
 * `FinancingPartner` — public-facing brand directory of pilot
 * financing partners (Tarmeez, Nayla, …). Sourced from a static
 * config on the backend ({@link financingPartners} in
 * `apps/backend/src/app/financing/financing.config.ts`) and served
 * via `GET /financing/partners`.
 *
 * Distinct from {@link LenderContact}:
 *
 * - `LenderContact` is the **DB-backed** record used by the lender
 *   inbox (`?i=<accessTokenHash>` URL) and by
 *   `FinancingApplication.offers[].lenderContactId`. It carries
 *   secret material (token hashes).
 * - `FinancingPartner` is the **display / marketing** shape — what
 *   the carrier dashboard shows in the partners strip, the apply
 *   form's pilot-partners list, and the review summary. It carries
 *   no secrets and is safe to serve to any authenticated user.
 *
 * The two will be reconciled later (see `docs/financing/roadmap.md`)
 * by promoting each partner to a real `Org`. For now they coexist:
 * partner ids are public identifiers committed to the config; lender
 * invite tokens stay in the LenderContact collection.
 */
export const financingPartnerSchema = z.object({
  /**
   * Public, committed random id (e.g. `"fp_a3f9e2b8"`). Used as the
   * React key + as a URL identifier when a route needs to say "which
   * partner is this". Not a secret — exposing it in the API and in
   * the URL is fine.
   */
  id: z.string().trim().min(1),

  /**
   * URL-safe lowercase slug (e.g. `"tarmeez"`, `"nayla"`). Stable
   * across rebrands; used for logo file naming and deep-link
   * fragments.
   */
  slug: z.string().trim().min(1).regex(/^[a-z][a-z0-9-]*$/),

  /** Display name (e.g. `"Tarmeez"`, `"Nayla"`). */
  name: nameSchema,

  /** One-line marketing copy shown next to the partner logo. */
  tagline: z.string().trim().min(1),

  /** Partner's marketing site — opened from the partners strip. */
  website: z.string().trim().url(),

  /**
   * Public logo URL. Expected to be a Cloudinary URL in production;
   * `undefined` while artwork is still being prepared. The carrier
   * dashboard hides the logo (falls back to a name-only chip) when
   * this is unset.
   */
  logoUrl: z.string().trim().url().optional(),

  /** Tailwind-safe `#RRGGBB` hex used as the chip / strip accent. */
  brandColor: z.string().trim().regex(/^#[0-9A-Fa-f]{6}$/),

  /**
   * Pilot contact address. Optional because we don't always have a
   * confirmed contact for early-stage partners; the backend doesn't
   * use this for the lender invite flow (that's `LenderContact`'s
   * job), so a missing email here is harmless.
   */
  email: emailSchema.optional(),
});

export type FinancingPartner = z.infer<typeof financingPartnerSchema>;
